import type {
  SessionEvent,
  ConversationModel,
  ConversationItem,
  Turn,
  TurnItem,
  ToolCallItem,
  SubagentGroup,
  SubagentGroupItem,
  AssistantTextItem,
  UserMessageItem,
  RemoteSteerableItem,
  AbortItem,
  AskUserItem,
  PermissionItem,
  ElicitationItem,
  PlanApprovalItem,
  PairedToolCall,
  UserMessageEvent,
  AssistantTurnStartEvent,
  AssistantMessageEvent,
  AssistantIntentEvent,
  ToolExecutionStartEvent,
  ToolExecutionCompleteEvent,
  AbortEvent,
  CustomAgentStartedEvent,
  CustomAgentCompletedEvent,
  CustomAgentFailedEvent,
  SessionStartEvent,
  SessionModelChangeEvent,
  SessionRemoteSteerableChangedEvent,
  PermissionRequestedEvent,
  PermissionCompletedEvent,
  ElicitationRequestedEvent,
  ElicitationCompletedEvent,
  ExitPlanModeRequestedEvent,
  ExitPlanModeCompletedEvent,
  UserInputRequestedEvent,
  UserInputCompletedEvent,
} from '../types/events'
import {isTurn} from '../types/events'
import type {NormalizedPrompt} from '../components/session-logs/prompt-log-utils'
import {normalizeAskUserArguments} from '../components/session-logs/prompt-log-utils'
import type {ExitPlanModeArguments, PermissionRequestArguments, RunSetupArgs} from '../types/session'
import {isTurnItemVisible, isUserMessageVisible} from './events/visibility'

type PromptItem = AskUserItem | PermissionItem | ElicitationItem | PlanApprovalItem

type PromptKind = 'ask_user' | 'permission' | 'elicitation' | 'plan_approval'

const PROMPT_KINDS = new Set<PromptKind>(['ask_user', 'permission', 'elicitation', 'plan_approval'])

function isPromptItem(item: TurnItem): item is PromptItem {
  return PROMPT_KINDS.has(item.kind as PromptKind)
}

function getPromptKey(item: PromptItem): string {
  switch (item.kind) {
    case 'ask_user':
      return item.toolCallId
    case 'permission':
      return item.requestId
    case 'elicitation':
      return item.toolCallId
    case 'plan_approval':
      return item.toolCallId ?? item.requestId
  }
}

/**
 * Extracts MCP tool name → display name mappings from run_setup tool calls
 * that have mcp_setup details with toolNamesToDisplayNames.
 */
function extractMcpMappingsFromItems(items: ConversationItem[]): Record<string, string> {
  const mappings: Record<string, string> = {}

  function walkItems(turnItems: TurnItem[]): void {
    for (const item of turnItems) {
      if (item.kind === 'tool_call' && item.toolCall.toolName === 'run_setup') {
        const args = item.toolCall.arguments as RunSetupArgs | undefined
        if (args?.details?.type === 'mcp_setup' && args.details.toolNamesToDisplayNames) {
          Object.assign(mappings, args.details.toolNamesToDisplayNames)
        }
      } else if (item.kind === 'subagent_group') {
        for (const turn of item.group.turns) {
          walkItems(turn.items)
        }
      }
    }
  }

  for (const conversationItem of items) {
    if (isTurn(conversationItem)) {
      walkItems(conversationItem.items)
    }
  }

  return mappings
}

function promptItemToNormalized(item: PromptItem): NormalizedPrompt {
  switch (item.kind) {
    case 'ask_user':
      return {
        toolCallId: item.toolCallId,
        toolName: 'ask_user',
        arguments: normalizeAskUserArguments({
          question: item.question,
          choices: item.choices ?? undefined,
          allowFreeform: item.allowFreeform,
        }),
        createdAt: item.timestamp,
      }
    case 'permission': {
      const permReq = item.requestEvent.data.permissionRequest as unknown as Record<string, unknown>
      const permToolCallId = (typeof permReq.toolCallId === 'string' ? permReq.toolCallId : null) ?? item.requestId
      return {
        toolCallId: permToolCallId,
        toolName: 'permission_request',
        arguments: permReq as unknown as PermissionRequestArguments,
        createdAt: item.timestamp,
      }
    }
    case 'elicitation':
      return {
        toolCallId: item.toolCallId,
        toolName: 'ask_user',
        arguments: normalizeAskUserArguments({
          message: item.message,
          requestedSchema: item.schema,
        }),
        createdAt: item.timestamp,
      }
    case 'plan_approval':
      return {
        toolCallId: item.toolCallId ?? item.requestId,
        toolName: 'exit_plan_mode',
        arguments: {
          summary: item.summary,
          planContent: item.planContent ?? undefined,
          actions: item.actions ?? undefined,
          recommendedAction: item.recommendedAction ?? undefined,
        } satisfies ExitPlanModeArguments,
        createdAt: item.timestamp,
      }
  }
}

interface ProcessingState {
  items: ConversationItem[]
  currentTurn: Turn | null
  toolCallIndex: Map<string, ToolCallItem>
  // Maps requestId/toolCallId → prompt item for completion pairing
  promptIndex: Map<string, PromptItem>
  // Maps custom_agent.started toolCallId → SubagentGroup
  activeSubagents: Map<string, SubagentGroupItem>
  currentIntent: string | null
  isWorking: boolean
  sessionModel: string | null
}

function createTurn(turnId: string | null): Turn {
  return {
    turnId,
    userMessage: null,
    items: [],
    intent: null,
    isComplete: false,
  }
}

function createUserMessageItem(event: UserMessageEvent): UserMessageItem {
  return {
    kind: 'user_message',
    id: event.id,
    timestamp: event.timestamp,
    content: event.data.content,
    source: event.data.source,
    isPending: event.pending === true,
    isDismissed: event.dismissed === true,
  }
}

function ensureCurrentTurn(state: ProcessingState): Turn {
  if (!state.currentTurn) {
    state.currentTurn = createTurn(null)
    state.items.push(state.currentTurn)
  }

  return state.currentTurn
}

// Finds the turn items array where an event should be appended.
// Events with parentToolCallId matching a custom_agent.started toolCallId
// are routed into that subagent's turns.
function resolveTargetTurnItems(state: ProcessingState, parentToolCallId?: string | null): TurnItem[] {
  if (parentToolCallId) {
    const subagentItem = state.activeSubagents.get(parentToolCallId)
    if (subagentItem) {
      const group = subagentItem.group
      if (group.turns.length === 0) {
        group.turns.push(createTurn(null))
      }

      const lastTurn = group.turns[group.turns.length - 1]
      if (!lastTurn) {
        throw new Error('Subagent group has no turns after ensuring one exists')
      }

      return lastTurn.items
    }
  }

  return ensureCurrentTurn(state).items
}

function processUserMessage(event: UserMessageEvent, state: ProcessingState): void {
  // Top-level user message: close current turn and start a new one
  if (state.currentTurn) {
    state.currentTurn.isComplete = true
  }

  const turn = createTurn(null)
  turn.userMessage = createUserMessageItem(event)
  state.currentTurn = turn
  state.items.push(turn)
}

function processAssistantTurnStart(event: AssistantTurnStartEvent, state: ProcessingState): void {
  const turn = ensureCurrentTurn(state)
  turn.turnId = event.data.turnId
  state.isWorking = true
}

function processAssistantMessage(event: AssistantMessageEvent, state: ProcessingState): void {
  const data = event.data

  const targetItems = resolveTargetTurnItems(state, data.parentToolCallId)

  // Append assistant text item if there's content or reasoning
  if (data.content || data.reasoningText) {
    const textItem: AssistantTextItem = {
      kind: 'assistant_text',
      id: `${event.id}-text`,
      timestamp: event.timestamp,
      messageId: data.messageId,
      content: data.content,
      reasoningText: data.reasoningText ?? null,
    }

    targetItems.push(textItem)
  }

  // Create items for each tool request
  if (data.toolRequests) {
    for (const req of data.toolRequests) {
      // Skip if already in index (dedup)
      if (state.toolCallIndex.has(req.toolCallId)) continue

      // Skip ask_user and exit_plan_mode — these are handled by their
      // standalone events (user_input.requested, exit_plan_mode.requested)
      // which have properly typed fields in the schema.
      if (req.name === 'ask_user' || req.name === 'exit_plan_mode') continue

      // Regular tool call — startEvent is null until tool.execution_start arrives
      const toolCall: PairedToolCall = {
        toolCallId: req.toolCallId,
        toolName: req.name,
        arguments: req.arguments,
        startEvent: null,
        completeEvent: null,
        parentToolCallId: data.parentToolCallId ?? null,
      }

      const toolCallItem: ToolCallItem = {
        kind: 'tool_call',
        id: `tool-${req.toolCallId}`,
        timestamp: event.timestamp,
        toolCall,
      }
      targetItems.push(toolCallItem)
      state.toolCallIndex.set(req.toolCallId, toolCallItem)
    }
  }
}

function processToolExecutionStart(event: ToolExecutionStartEvent, state: ProcessingState): void {
  const data = event.data

  // If already registered from assistant.message toolRequests, backfill the startEvent
  const existing = state.toolCallIndex.get(data.toolCallId)
  if (existing) {
    existing.toolCall.startEvent = event
    return
  }

  const targetItems = resolveTargetTurnItems(state, data.parentToolCallId)

  const toolCall: PairedToolCall = {
    toolCallId: data.toolCallId,
    toolName: data.toolName,
    arguments: data.arguments,
    startEvent: event,
    completeEvent: null,
    parentToolCallId: data.parentToolCallId ?? null,
  }

  const toolCallItem: ToolCallItem = {
    kind: 'tool_call',
    id: `tool-${data.toolCallId}`,
    timestamp: event.timestamp,
    toolCall,
  }

  targetItems.push(toolCallItem)
  state.toolCallIndex.set(data.toolCallId, toolCallItem)
}

function processToolExecutionComplete(event: ToolExecutionCompleteEvent, state: ProcessingState): void {
  const existing = state.toolCallIndex.get(event.data.toolCallId)
  if (existing) {
    existing.toolCall.completeEvent = event
  }
}

function processAssistantIntent(event: AssistantIntentEvent, state: ProcessingState): void {
  state.currentIntent = event.data.intent
  const turn = ensureCurrentTurn(state)
  turn.intent = event.data.intent
}

function processAssistantTurnEnd(_event: SessionEvent, state: ProcessingState): void {
  if (state.currentTurn) {
    state.currentTurn.isComplete = true
  }
}

function processSessionIdle(_event: SessionEvent, state: ProcessingState): void {
  state.isWorking = false
}

function processAbort(event: AbortEvent, state: ProcessingState): void {
  state.isWorking = false
  const abortItem: AbortItem = {
    kind: 'abort',
    id: event.id,
    timestamp: event.timestamp,
    reason: event.data.reason,
  }

  state.items.push(abortItem)
}

function processCustomAgentStarted(event: CustomAgentStartedEvent, state: ProcessingState): void {
  const data = event.data

  const group: SubagentGroup = {
    agentId: data.toolCallId,
    agentName: data.agentName,
    agentDisplayName: data.agentDisplayName,
    description: data.agentDescription,
    startEvent: event,
    endEvent: null,
    turns: [],
    isPending: true,
  }

  const subagentItem: SubagentGroupItem = {
    kind: 'subagent_group',
    id: `subagent-${data.toolCallId}`,
    timestamp: event.timestamp,
    group,
  }

  ensureCurrentTurn(state).items.push(subagentItem)
  state.activeSubagents.set(data.toolCallId, subagentItem)
}

function processCustomAgentCompleted(event: CustomAgentCompletedEvent, state: ProcessingState): void {
  const subagentItem = state.activeSubagents.get(event.data.toolCallId)
  if (subagentItem) {
    subagentItem.group.endEvent = event
    subagentItem.group.isPending = false
  }
}

function processCustomAgentFailed(event: CustomAgentFailedEvent, state: ProcessingState): void {
  const subagentItem = state.activeSubagents.get(event.data.toolCallId)
  if (subagentItem) {
    subagentItem.group.endEvent = event
    subagentItem.group.isPending = false
  }
}

function processUserInputRequested(event: UserInputRequestedEvent, state: ProcessingState): void {
  const data = event.data
  const key = data.toolCallId ?? data.requestId

  const askItem: AskUserItem = {
    kind: 'ask_user',
    id: `ask-${key}`,
    timestamp: event.timestamp,
    toolCallId: key,
    question: data.question,
    choices: data.choices ?? null,
    allowFreeform: data.allowFreeform ?? true,
    completedEvent: null,
  }

  const targetItems = resolveTargetTurnItems(state)
  targetItems.push(askItem)
  state.promptIndex.set(data.requestId, askItem)
  if (data.toolCallId) {
    state.promptIndex.set(data.toolCallId, askItem)
  }
  state.isWorking = false
}

function processUserInputCompleted(event: UserInputCompletedEvent, state: ProcessingState): void {
  const item = state.promptIndex.get(event.data.requestId)
  if (item && item.kind === 'ask_user') {
    item.completedEvent = event
  }
}

function processPermissionRequested(event: PermissionRequestedEvent, state: ProcessingState): void {
  const data = event.data
  const permItem: PermissionItem = {
    kind: 'permission',
    id: `perm-${data.requestId}`,
    timestamp: event.timestamp,
    requestId: data.requestId,
    requestEvent: event,
    completedEvent: null,
  }

  const targetItems = resolveTargetTurnItems(state)
  targetItems.push(permItem)
  state.promptIndex.set(data.requestId, permItem)
  state.isWorking = false
}

function processPermissionCompleted(event: PermissionCompletedEvent, state: ProcessingState): void {
  const item = state.promptIndex.get(event.data.requestId)
  if (item && item.kind === 'permission') {
    item.completedEvent = event
  }
}

function processElicitationRequested(event: ElicitationRequestedEvent, state: ProcessingState): void {
  const data = event.data
  const elicitItem: ElicitationItem = {
    kind: 'elicitation',
    id: `elicit-${data.requestId}`,
    timestamp: event.timestamp,
    requestId: data.requestId,
    message: data.message,
    schema: data.requestedSchema,
    toolCallId: data.toolCallId,
    completedEvent: null,
  }

  const targetItems = resolveTargetTurnItems(state)
  targetItems.push(elicitItem)
  state.promptIndex.set(data.requestId, elicitItem)
  state.isWorking = false
}

function processElicitationCompleted(event: ElicitationCompletedEvent, state: ProcessingState): void {
  const item = state.promptIndex.get(event.data.requestId)
  if (item && item.kind === 'elicitation') {
    item.completedEvent = event
  }
}

function processExitPlanModeRequested(event: ExitPlanModeRequestedEvent, state: ProcessingState): void {
  const data = event.data

  const planItem: PlanApprovalItem = {
    kind: 'plan_approval',
    id: `plan-${data.requestId}`,
    timestamp: event.timestamp,
    requestId: data.requestId,
    toolCallId: data.toolCallId ?? null,
    summary: data.summary,
    planContent: data.planContent ?? null,
    actions: data.actions ?? null,
    recommendedAction: data.recommendedAction ?? null,
    completedEvent: null,
  }

  const targetItems = resolveTargetTurnItems(state)
  targetItems.push(planItem)
  state.promptIndex.set(data.requestId, planItem)
  if (data.toolCallId) {
    state.promptIndex.set(data.toolCallId, planItem)
  }
  state.isWorking = false
}

function processExitPlanModeCompleted(event: ExitPlanModeCompletedEvent, state: ProcessingState): void {
  const item = state.promptIndex.get(event.data.requestId)
  if (item && item.kind === 'plan_approval') {
    item.completedEvent = event
  }
}

function processSessionStart(event: SessionStartEvent, state: ProcessingState): void {
  if (event.data.selectedModel) {
    state.sessionModel = event.data.selectedModel
  }
}

function processSessionModelChange(event: SessionModelChangeEvent, state: ProcessingState): void {
  state.sessionModel = event.data.newModel
}

function processRemoteSteerableChanged(event: SessionRemoteSteerableChangedEvent, state: ProcessingState): void {
  const targetItems = resolveTargetTurnItems(state)
  const item: RemoteSteerableItem = {
    kind: 'remote_steerable',
    id: event.id,
    timestamp: event.timestamp,
    enabled: event.data.remoteSteerable,
  }
  targetItems.push(item)

  if (event.data.remoteSteerable) {
    state.isWorking = false
  }
}

export function buildConversationModel(events: SessionEvent[]): ConversationModel {
  const state: ProcessingState = {
    items: [],
    currentTurn: null,
    toolCallIndex: new Map(),
    promptIndex: new Map(),
    activeSubagents: new Map(),
    currentIntent: null,
    isWorking: false,
    sessionModel: null,
  }

  for (const event of events) {
    try {
      switch (event.type) {
        case 'user.message':
          processUserMessage(event, state)
          break
        case 'assistant.turn_start':
          processAssistantTurnStart(event, state)
          break
        case 'assistant.message':
          processAssistantMessage(event, state)
          break
        case 'tool.execution_start':
          processToolExecutionStart(event, state)
          break
        case 'tool.execution_complete':
          processToolExecutionComplete(event, state)
          break
        case 'assistant.intent':
          processAssistantIntent(event, state)
          break
        case 'assistant.turn_end':
          processAssistantTurnEnd(event, state)
          break
        case 'session.idle':
          processSessionIdle(event, state)
          break
        case 'abort':
          processAbort(event, state)
          break
        case 'custom_agent.started':
          processCustomAgentStarted(event, state)
          break
        case 'custom_agent.completed':
          processCustomAgentCompleted(event, state)
          break
        case 'custom_agent.failed':
          processCustomAgentFailed(event, state)
          break
        case 'session.start':
          processSessionStart(event, state)
          break
        case 'session.model_change':
          processSessionModelChange(event, state)
          break
        case 'session.remote_steerable_changed':
          processRemoteSteerableChanged(event, state)
          break
        case 'user_input.requested':
          processUserInputRequested(event, state)
          break
        case 'user_input.completed':
          processUserInputCompleted(event, state)
          break
        case 'permission.requested':
          processPermissionRequested(event, state)
          break
        case 'permission.completed':
          processPermissionCompleted(event, state)
          break
        case 'elicitation.requested':
          processElicitationRequested(event, state)
          break
        case 'elicitation.completed':
          processElicitationCompleted(event, state)
          break
        case 'exit_plan_mode.requested':
          processExitPlanModeRequested(event, state)
          break
        case 'exit_plan_mode.completed':
          processExitPlanModeCompleted(event, state)
          break
        // Ignored event types for MVP:
        // session.resume, session.error, session.info, session.handoff,
        // session.truncation, session.import_legacy, session.title_changed, session.shutdown,
        // hook.start, hook.end, system.message, assistant.usage, tool.user_requested,
        // tool.execution_partial_result, custom_agent.selected
        default:
          break
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to process event ${event.id} (type=${event.type}):`, err)
    }
  }

  // Compute pending prompt by walking turns in order (FIFO)
  const serverResolvedIds = new Set<string>()
  let pendingPrompt: NormalizedPrompt | null = null
  const allUnresolvedPrompts: NormalizedPrompt[] = []

  for (const conversationItem of state.items) {
    if (!isTurn(conversationItem)) continue

    for (const item of conversationItem.items) {
      if (!isPromptItem(item)) continue

      const key = getPromptKey(item)

      if (item.completedEvent !== null) {
        serverResolvedIds.add(key)
      } else {
        const normalized = promptItemToNormalized(item)
        allUnresolvedPrompts.push(normalized)
        if (!pendingPrompt) {
          pendingPrompt = normalized
        }
      }
    }
  }

  const hasVisibleContent = state.items.some(item =>
    isTurn(item)
      ? (item.userMessage !== null && isUserMessageVisible(item.userMessage)) || item.items.some(isTurnItemVisible)
      : true,
  )

  return {
    items: state.items,
    currentIntent: state.currentIntent,
    isWorking: state.isWorking,
    sessionModel: state.sessionModel,
    pendingPrompt,
    allUnresolvedPrompts,
    serverResolvedIds,
    hasVisibleContent,
    mcpToolNameMappings: extractMcpMappingsFromItems(state.items),
  }
}
